import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    Drawer,
    IconButton,
    Typography,
    Chip,
    Stack,
    Divider,
    CircularProgress,
    Tooltip,
    useMediaQuery,
    TextField,
    InputAdornment,
    MenuItem,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../utils/Api";

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

const RewardOffers = () => {
    const token = localStorage.getItem("token");

    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState([]);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const [q, setQ] = useState("");
    const [filterStatus, setFilterStatus] = useState("ALL"); // local filter only
    const [filterRewardNo, setFilterRewardNo] = useState("ALL");
    const [filterDiscountType, setFilterDiscountType] = useState("ALL");
    const [filterCouponType, setFilterCouponType] = useState("ALL");
    const [filterFreebie, setFilterFreebie] = useState("ALL"); // ALL | YES | NO

    const isMdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

    const getRewards = async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.get(
                `${API.BASE_URL}auto-coupon-allocation?page=1&limit=100&status=ACTIVE&nthOrderNumber=true`,
                { headers }
            );
            setOffers(Array.isArray(res?.data?.data) ? res.data.data : []);
        } catch (e) {
            console.error(e);
            toast.error("Failed to fetch offers", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getRewards();
    }, []);

    const handleOpen = (offer) => {
        setSelected(offer);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelected(null);
    };

    const journeyNo = useMemo(() => {
        const o = selected;
        if (!o) return null;
        return o?.nthOrderNumber != null
            ? o.nthOrderNumber
            : o?.journeyNumber != null
                ? o.journeyNumber
                : null;
    }, [selected]);

    const rules = useMemo(() => {
        if (!selected) return [];
        const o = selected;
        const parts = [];

        if (o?.couponType) parts.push(`Coupon: ${o.couponType}`);
        if (o?.discountType) parts.push(`Discount Type: ${o.discountType}`);

        if (o?.discountType === "PERCENT" && o?.discountPercent != null)
            parts.push(`Percent: ${o.discountPercent}%`);
        if (o?.discountType === "PRICE" && o?.discountPrice != null)
            parts.push(`Price OFF: ₹${o.discountPrice}`);

        if (o?.minCartValue != null) parts.push(`Min Cart: ₹${o.minCartValue}`);
        if (o?.minOrderAmount != null) parts.push(`Min Order: ₹${o.minOrderAmount}`);
        if (o?.maxOrderAmount != null) parts.push(`Max Order: ₹${o.maxOrderAmount}`);

        if (o?.nthOrderNumber != null) parts.push(`Reward On Order: ${o.nthOrderNumber}`);
        if (o?.usageType) parts.push(`Usage: ${o.usageType}`);
        if (o?.validityInDays != null) parts.push(`Validity: ${o.validityInDays} day(s)`);

        parts.push(`Freebie: ${o?.isFreebieCoupon ? "YES" : "NO"}`);
        if (o?.freebieProduct?.name) parts.push(`Freebie Item: ${o.freebieProduct.name}`);
        if (o?.freebieQuantity != null) parts.push(`Freebie Qty: ${o.freebieQuantity}`);

        return parts;
    }, [selected]);

    const excludedProducts = useMemo(() => {
        const ex = selected?.excludedProducts;
        if (!ex) return [];
        return Array.isArray(ex) ? ex : [];
    }, [selected]);

    const rewardNoOptions = useMemo(() => {
        const set = new Set();
        offers.forEach((o) => {
            const j =
                o?.nthOrderNumber != null
                    ? o.nthOrderNumber
                    : o?.journeyNumber != null
                        ? o.journeyNumber
                        : null;
            if (j != null) set.add(String(j));
        });
        return Array.from(set).sort((a, b) => Number(a) - Number(b));
    }, [offers]);

    const discountTypeOptions = useMemo(() => {
        const set = new Set();
        offers.forEach((o) => {
            if (o?.discountType) set.add(String(o.discountType));
        });
        return Array.from(set).sort();
    }, [offers]);

    const couponTypeOptions = useMemo(() => {
        const set = new Set();
        offers.forEach((o) => {
            if (o?.couponType) set.add(String(o.couponType));
        });
        return Array.from(set).sort();
    }, [offers]);

    const filteredOffers = useMemo(() => {
        const query = q.trim().toLowerCase();

        return offers.filter((o) => {
            const jNo =
                o?.nthOrderNumber != null
                    ? o.nthOrderNumber
                    : o?.journeyNumber != null
                        ? o.journeyNumber
                        : null;

            // Search (title/description/couponType/discountType/freebie product name)
            if (query) {
                const hay = [
                    safeStr(o?.title),
                    safeStr(o?.description),
                    safeStr(o?.couponType),
                    safeStr(o?.discountType),
                    safeStr(o?.freebieProduct?.name),
                ]
                    .join(" ")
                    .toLowerCase();

                if (!hay.includes(query)) return false;
            }

            // Status (local filter; since API already returns ACTIVE, this is optional)
            if (filterStatus !== "ALL") {
                const st = safeStr(o?.status).toUpperCase();
                if (st !== filterStatus) return false;
            }

            // Reward No
            if (filterRewardNo !== "ALL") {
                if (String(jNo) !== String(filterRewardNo)) return false;
            }

            // Discount Type
            if (filterDiscountType !== "ALL") {
                if (safeStr(o?.discountType) !== filterDiscountType) return false;
            }

            // Coupon Type
            if (filterCouponType !== "ALL") {
                if (safeStr(o?.couponType) !== filterCouponType) return false;
            }

            // Freebie
            if (filterFreebie !== "ALL") {
                const yes = !!o?.isFreebieCoupon;
                if (filterFreebie === "YES" && !yes) return false;
                if (filterFreebie === "NO" && yes) return false;
            }

            return true;
        });
    }, [
        offers,
        q,
        filterStatus,
        filterRewardNo,
        filterDiscountType,
        filterCouponType,
        filterFreebie,
    ]);

    const clearFilters = () => {
        setQ("");
        setFilterStatus("ALL");
        setFilterRewardNo("ALL");
        setFilterDiscountType("ALL");
        setFilterCouponType("ALL");
        setFilterFreebie("ALL");
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, md: 22 } }}>
                        Reward Offers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Search and filter offers. Tap an offer to view details in the sidebar.
                    </Typography>
                </Box>

                {/* Right Section */}
                <Box sx={{ flex: 1 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search by title, description, coupon type..."
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: q ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setQ("")}
                                                aria-label="clear search"
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Coupon"
                                value={filterCouponType}
                                onChange={(e) => setFilterCouponType(e.target.value)}
                            >
                                <MenuItem value="ALL">All</MenuItem>
                                {couponTypeOptions.map((v) => (
                                    <MenuItem key={v} value={v}>
                                        {v}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : filteredOffers?.length === 0 ? (
                <Box
                    sx={{
                        p: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                        bgcolor: "background.paper",
                    }}
                >
                    <Typography sx={{ fontWeight: 800 }}>No offers match your search/filters</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Try clearing filters or changing the search term.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {filteredOffers.map((o) => {
                        const jNo =
                            o?.nthOrderNumber != null
                                ? o.nthOrderNumber
                                : o?.journeyNumber != null
                                    ? o.journeyNumber
                                    : null;

                        return (
                            <Grid item xs={6} sm={4} lg={3} key={o?._id}>
                                <Box
                                    onClick={() => handleOpen(o)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") handleOpen(o);
                                    }}
                                    sx={{
                                        cursor: "pointer",
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        position: "relative",
                                        bgcolor: "background.paper",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        transition: "transform 180ms ease, box-shadow 180ms ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                                        },
                                        "&:focus-visible": {
                                            outline: "3px solid",
                                            outlineColor: "primary.main",
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    {jNo != null ? (
                                        <Chip
                                            label={`Reward ${jNo}`}
                                            size="small"
                                            sx={{
                                                position: "absolute",
                                                top: 10,
                                                left: 10,
                                                zIndex: 3,
                                                fontWeight: 900,
                                                borderRadius: 2,
                                                bgcolor: "rgba(0,0,0,0.65)",
                                                color: "#fff",
                                                border: "1px solid rgba(255,255,255,0.18)",
                                                "& .MuiChip-label": { px: 1.1 },
                                            }}
                                        />
                                    ) : null}

                                    <Box
                                        component="img"
                                        src={o?.displayImageUrl}
                                        alt={o?.title || "Coupon"}
                                        loading="lazy"
                                        sx={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            objectFit: "contain",
                                            backgroundColor: "background.default",
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                        }}
                                    />

                                    {/* Light hover overlay */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            zIndex: 2,
                                            opacity: 0,
                                            transition: "opacity 180ms ease",
                                            background:
                                                "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.10), rgba(0,0,0,0))",
                                            "&:hover": { opacity: 1 },
                                        }}
                                    />
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 420, md: 460 },
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    },
                }}
            >
                {/* Sticky Header */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        bgcolor: "background.paper",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        px: 2.2,
                        py: 1.6,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 950,
                                fontSize: 18,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {selected?.title || "Offer Details"}
                        </Typography>

                        {journeyNo != null ? (
                            <Chip
                                size="small"
                                label={`Reward ${journeyNo}`}
                                sx={{
                                    mt: 0.8,
                                    height: 22,
                                    fontWeight: 800,
                                    bgcolor: "action.hover",
                                }}
                            />
                        ) : null}
                    </Box>

                    <IconButton
                        onClick={handleClose}
                        aria-label="close"
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        p: 2.2,
                        pb: 3,
                        flex: 1,
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    {selected?.displayImageUrl ? (
                        <Box
                            component="img"
                            src={selected.displayImageUrl}
                            alt={selected?.title || "Coupon"}
                            sx={{
                                width: "60%",
                                height: "auto",
                                display: "block",
                                objectFit: "contain",
                                backgroundColor: "background.default",
                                justifySelf: "center",
                                mx: "auto",
                                mb: 1.5,
                            }}
                        />
                    ) : null}

                    {/* Description */}
                    {selected?.description ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                            {selected.description}
                        </Typography>
                    ) : null}

                    {/* Rules */}
                    {rules.length > 0 ? (
                        <Box sx={{ p: 1.6, borderRadius: 3, bgcolor: "action.hover", mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
                                Rules
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {rules.map((r, idx) => (
                                    <Chip
                                        key={idx}
                                        label={r}
                                        size="small"
                                        variant="outlined"
                                        sx={{ bgcolor: "background.paper" }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ) : null}

                    {/* Applicable Categories */}
                    {(selected?.applicableCategories?.length ?? 0) > 0 ? (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
                                Applicable Categories
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {selected.applicableCategories.map((c) => (
                                    <Chip
                                        key={c?._id || safeStr(c?.name)}
                                        label={c?.name || "Category"}
                                        size="small"
                                        sx={{ bgcolor: "action.hover", fontWeight: 700 }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ) : null}

                    {(selected?.applicableProducts?.length ?? 0) > 0 ? (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
                                Applicable Products
                            </Typography>

                            <Stack spacing={1}>
                                {selected.applicableProducts.map((p) => (
                                    <Box
                                        key={p?._id || safeStr(p?.name)}
                                        sx={{
                                            p: 1.4,
                                            borderRadius: 3,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 900, lineHeight: 1.25 }}>
                                            {p?.name || "Product"}
                                        </Typography>

                                        {p?.offerPrice != null || p?.mrp != null ? (
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.6 }}>
                                                {p?.offerPrice != null ? (
                                                    <Chip
                                                        size="small"
                                                        label={`Offer ₹${p.offerPrice}`}
                                                        sx={{ fontWeight: 900, bgcolor: "action.hover" }}
                                                    />
                                                ) : null}

                                                {p?.mrp != null ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        MRP: ₹{p.mrp}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                        ) : null}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ) : null}

                    {/* Excluded Products */}
                    {Array.isArray(selected?.excludedProducts) && selected.excludedProducts.length > 0 ? (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
                                Excluded Products
                            </Typography>

                            {typeof selected.excludedProducts[0] === "object" ? (
                                <Stack spacing={0.6}>
                                    {selected.excludedProducts.map((p, idx) => (
                                        <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                            <Box
                                                sx={{
                                                    mt: "7px",
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: "50%",
                                                    bgcolor: "text.disabled",
                                                    flex: "0 0 auto",
                                                }}
                                            />
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                                {typeof p === "object" ? p?.name || "Product" : safeStr(p)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {selected.excludedProducts.map((id) => (
                                        <Chip key={safeStr(id)} label={safeStr(id)} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    ) : null}

                    {/* Terms */}
                    {(selected?.termsAndConditions?.length ?? 0) > 0 ? (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
                                Terms & Conditions
                            </Typography>

                            <Stack spacing={0.7}>
                                {selected.termsAndConditions.map((t, idx) => (
                                    <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                        <Box
                                            sx={{
                                                mt: "7px",
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                bgcolor: "text.disabled",
                                                flex: "0 0 auto",
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                            {t}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ) : null}
                </Box>
            </Drawer>
        </Box>
    );
};

export default RewardOffers;